import { useRouter } from 'next/router';
import { useQuery } from 'react-query';

import { boardsServices } from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';
import { showError } from '@utils';
import { useAssignees } from 'hooks/common';
import {
  getDefleetQueryParams,
  getDefleetRequestPayload,
  getColumns,
  normaliseDefleetTasks,
} from './utils';
import { DEFLEET_PAGE_SIZE, DEFLEET_TABS } from './constants';

export const useDefleetOperations = () => {
  const {
    assignees,
    isLoading: isAssigneesLoading,
    isFetched: isAssigneesFetched,
  } = useAssignees();
  const router = useRouter();
  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || DEFLEET_PAGE_SIZE;
  const assignee = router.query?.assignee;
  const orderBy = router.query?.orderBy;
  const order = router.query?.order;
  const brand = router.query?.brand;
  const model = router.query?.model;
  const year = router.query?.year && Number(router.query?.year);
  const target = router.query?.target;
  const query = router.query?.query;

  const subscriptionStatus =
    router.query?.subscriptionStatus &&
    Number(router.query?.subscriptionStatus);
  const status = router.query?.status && Number(router.query?.status);
  const promisedReturnDate = router.query?.promisedReturnDate;
  const returnDate = router.query?.returnDate;
  const monthlyFee = router.query?.monthlyFee;
  const archived = router.query?.archived;

  const board = router.query?.board || Object.keys(DEFLEET_TABS)[0];

  if (!router.query?.brand && router.query?.model) {
    delete router.query.model;
    router.push(router);
  }

  const params = {
    page,
    pageSize: parseInt(pageSize, 10) || DEFLEET_PAGE_SIZE,
    assignee,
    orderBy,
    order,
    board,
    query,
    brand,
    model,
    year,
    target,
    subscriptionStatus,
    status,
    promisedReturnDate,
    returnDate,
    monthlyFee,
    archived,
  };

  const cleanParams = getDefleetQueryParams({
    ...params,
  });

  const defleetResponse = useQuery(
    [QUERY_KEYS.GET_DEFLEET_KEY, cleanParams],
    () =>
      boardsServices.getBoardTasks({
        boardId: DEFLEET_TABS[board].id,
        params: getDefleetQueryParams(cleanParams),
        payload: getDefleetRequestPayload({
          ...cleanParams,
          boardId: DEFLEET_TABS[board].id,
          boardType: DEFLEET_TABS[board].type,
        }),
      }),
    { enabled: isAssigneesFetched }
  );

  const enumsResponse = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, DEFLEET_TABS[board].id],
    () => boardsServices.getEnumsPerBoardId(DEFLEET_TABS[board].id),
    { enabled: router.isReady }
  );

  const defleetTasks = normaliseDefleetTasks(
    defleetResponse?.data?.data || [],
    assignees
  );

  const statuses = DEFLEET_TABS[board].statusOrder.reduce((acc, cur) => {
    acc.push({
      text: DEFLEET_TABS[board].statuses[cur].label,
      value: Number(cur),
    });
    return acc;
  }, []);

  const updateTask = async (payload = {}) => {
    try {
      if (!payload?.cardId) return;

      const taskId = payload?.cardId;
      delete payload?.cardId;
      await boardsServices.updateTask(taskId, payload);

      await boardsServices.updateTask(taskId, payload);
    } catch (error) {
      showError(error);
    }
  };

  return {
    page,
    pageSize: parseInt(pageSize, 10) || DEFLEET_PAGE_SIZE,
    assignees,
    total: defleetResponse?.data?.meta?.total,
    loading: defleetResponse?.isLoading || isAssigneesLoading,
    error: defleetResponse?.error,
    isSuccess: defleetResponse?.isSuccess,
    defleetTasks,
    board,
    statuses,
    enums: enumsResponse?.data?.data || {},
    columns: getColumns(defleetTasks || [], board),
    refresh: defleetResponse.refetch,
    updateTask,
  };
};
